import {
  type ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import { PATIENT_TRANSACTION_RESOURCE_TYPE } from '../../../destination/entities/patient-invoices';
import { SourceEntity } from '../../../source/source-entity';
import {
  isISODateType,
  ISODateType,
  Timezone,
  toFloat,
  TypeGuard,
} from '@principle-theorem/shared';
import { flow, isNumber, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import {
  convertKeysToCamelCaseFn,
  convertNullToUndefinedFn,
  convertValueFn,
} from '../../../source/source-helpers';

export const PATIENT_TRANSACTIONS_SOURCE_ENTITY = SourceEntity.init({
  metadata: {
    label: 'Patient Transactions List',
    description: '',
    idPrefix: PATIENT_TRANSACTION_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

const PATIENT_TRANSACTION_QUERY = `
SELECT
  patient_transactions.*,
  treatment_codes.display_code,
  treatment_codes.item_code,
  treatment_codes.service_description,
  treatment_codes.code_type,
  treatment_codes.payment_code_type
FROM (
  SELECT
    transid AS transaction_id,
    respons_person_id AS response_patient_id,
    patient_persion_id AS patient_person_id,
    visit_id,
    entry_date AS created_date,
    service_date,
    tx_code_id AS treatment_code_id,
    producer_employee_id AS producer_staff_id,
    provider_employee_id AS provider_staff_id,
    ar_owner_employee_id AS owner_staff_id,
    convert_to_decimal(total_amount) AS total_amount,
    groupid AS group_id
  FROM
    _ledger_history_
  WHERE transid IS NOT NULL AND trans_type = 3 OR trans_type = 4
) AS patient_transactions
LEFT JOIN (
  SELECT
    tx_code_id AS code_id,
    NULLIF(user_id, '') AS display_code,
    convert_to_text(medical_code) AS item_code,
    description AS service_description,
    code_type,
    pmt_code_type AS payment_code_type
  FROM
    _tx_code_defs_
) AS treatment_codes ON patient_transactions.treatment_code_id = treatment_codes.code_id
`;

export interface IPracticeWorksTransaction {
  transactionId: number;
  responsePatientId: number;
  patientPersonId: number;
  visitId: number;
  createdDate: ISODateType;
  serviceDate: ISODateType;
  treatmentCodeId: number;
  transactionDescription?: string;
  producerStaffId: number;
  providerStaffId: number;
  ownerStaffId: number;
  totalAmount: number;
  groupId: number;
  displayCode?: string;
  itemCode?: string;
  serviceDescription: string;
  codeType: number;
  paymentCodeType: number;
}

export interface IPracticeWorksTransactionFilters {
  responsePatientId: string;
  patientPersonId: string;
}

export function isPracticeWorksTransaction(
  item: unknown,
  logError: boolean
): item is IPracticeWorksTransaction {
  return TypeGuard.interface<IPracticeWorksTransaction>(
    {
      transactionId: isNumber,
      responsePatientId: isNumber,
      patientPersonId: isNumber,
      visitId: isNumber,
      createdDate: isISODateType,
      serviceDate: isISODateType,
      treatmentCodeId: isNumber,
      transactionDescription: TypeGuard.nilOr(isString),
      producerStaffId: isNumber,
      providerStaffId: isNumber,
      ownerStaffId: isNumber,
      totalAmount: isNumber,
      groupId: isNumber,
      displayCode: TypeGuard.nilOr(isString),
      itemCode: TypeGuard.nilOr(isString),
      serviceDescription: isString,
      codeType: isNumber,
      paymentCodeType: isNumber,
    },
    logError
  )(item);
}

export type PracticeWorksTransaction = ISourceEntityTypes<
  IPracticeWorksTransaction,
  object,
  IPracticeWorksTransactionFilters
>;

export class PatientTransactionsSourceEntity extends BaseSourceEntity<PracticeWorksTransaction> {
  sourceEntity = PATIENT_TRANSACTIONS_SOURCE_ENTITY;
  entityResourceType = PATIENT_TRANSACTION_RESOURCE_TYPE;
  sourceQuery = PATIENT_TRANSACTION_QUERY;
  verifySourceFn = isPracticeWorksTransaction;

  override transformDataFn = flow([
    convertKeysToCamelCaseFn(),
    convertNullToUndefinedFn(),
    convertValueFn(toFloat, 'totalAmount'),
    convertValueFn((val) => {
      if (isString(val) && val.length === 2) {
        return `0${val}`;
      }
      return val;
    }, 'itemCode'),
  ]);

  translate(_: IPracticeWorksTransaction): object {
    return {};
  }

  getSourceLabel(data: IPracticeWorksTransaction): string {
    return `${data.responsePatientId} ${data.transactionId}`;
  }

  getSourceRecordId(data: IPracticeWorksTransaction): number {
    return data.transactionId;
  }

  getFilterData(
    data: IPracticeWorksTransaction,
    _: Timezone
  ): IPracticeWorksTransactionFilters {
    return {
      responsePatientId: data.responsePatientId.toString(),
      patientPersonId: data.patientPersonId.toString(),
    };
  }
}
